import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import DataTable from "@/components/common/DataTable";
import EmptyState from "@/components/common/EmptyState";
import StatusBadge from "@/components/common/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Handshake } from "lucide-react";

export default function Partners() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data } = await api.get("/partners"); setRows(data); }
    catch (e) { setError(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/partners/${id}`); setDetail(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const columns = [
    { key: "full_name", label: "Partner", render: (r) => <div><div className="font-semibold text-slate-800">{r.full_name}</div><div className="text-xs text-slate-400">{r.email}</div></div> },
    { key: "mobile", label: "Mobile", mobileHide: true, render: (r) => r.mobile || "—" },
    { key: "commission_plan", label: "Plan", mobileHide: true, render: (r) => r.commission_plan || "—" },
    { key: "leads_submitted", label: "Leads", render: (r) => r.leads_submitted },
    { key: "expected_commission", label: "Commission", render: (r) => fmtCurrency(r.expected_commission) },
    { key: "active", label: "Status", render: (r) => <StatusBadge status={r.active ? "Approved" : "Reversed"} label={r.active ? "Active" : "Inactive"} /> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Partners" subtitle="Sales partners and their submitted leads & commission." testid="partners-header" />
      <DataTable columns={columns} rows={rows} loading={loading} error={error} onRetry={load}
        onRowClick={(r) => openDetail(r.id)} testid="partners-table"
        empty={{ title: "No partners yet", description: "Sales partners created by the admin will appear here.", icon: Handshake }} />

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="partner-detail-dialog">
          <DialogHeader><DialogTitle>{detail?.full_name}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Submitted leads ({detail?.leads?.length || 0})</h4>
              {(!detail?.leads || detail.leads.length === 0) ? <p className="text-sm text-slate-400">No leads submitted</p> : (
                <div className="space-y-1">{detail.leads.map((l) => <div key={l.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1"><span className="text-slate-700">{l.org_name}</span><StatusBadge status={l.status} /></div>)}</div>
              )}
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Commission entries</h4>
              {(!detail?.ledger || detail.ledger.length === 0) ? <p className="text-sm text-slate-400">No commission yet</p> : (
                <div className="space-y-1">{detail.ledger.map((c) => <div key={c.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1"><span className="text-slate-700">{c.deal_title}</span><span className="flex items-center gap-2"><b>{fmtCurrency(c.amount)}</b><StatusBadge status={c.status} /></span></div>)}</div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
